import { ChevronDownIcon, EditIcon, Icon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useMediaQuery,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { usePDF } from "@react-pdf/renderer";
import {
  capitalize,
  get,
  includes,
  isEmpty,
  startCase,
  truncate,
} from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { BsFileEarmarkMedical, BsThreeDotsVertical } from "react-icons/bs";
import { MdDetails, MdRequestPage, MdUpdate } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import UpdateCommodity from "../modals/commodity/update/UpdateCommodity";
import CommoditiesPDF from "../pdf/CommoditiesPDF";
import { CSVLink } from "react-csv";
import { FaRegFilePdf } from "react-icons/fa";
import { PRODUCT_CATEGORIES } from "../../utils/constants";
// @ts-ignore
import ReCAPTCHA from "react-google-recaptcha";
import { useMutation } from "@apollo/client";
import { REQUEST_PRICE_UPDATE } from "./mutations";

type CommoditiesTableContainerProps = {
  commodities: any[];
  isPublic?: boolean;
};

const CommoditiesTableContainer = (props: CommoditiesTableContainerProps) => {
  const { commodities, isPublic } = props;
  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");
  const [selectUpdateItem, setSelectUpdateItem] = useState<string>("");
  const [selectedCheckboxItems, setSelectedCheckboxItems] = useState<any>([]);
  const [exportPDF, setExportPDF] = useState<any>([]);
  const [exportCSV, setExportCSV] = useState<any>([]);
  const [captchaToken, setCaptchaToken] = useState<any>([]);
  const toast = useToast();

  const [requestPriceUpdate, { loading: requestingUpdate }] =
    useMutation(REQUEST_PRICE_UPDATE);

  const cancelRef = useRef<any>();
  const navigate = useNavigate();
  const {
    isOpen: isUpdateModalOpen,
    onOpen: onOpenUpdateModal,
    onClose: onCloseUpdateModal,
  } = useDisclosure();

  const {
    isOpen: isRequestUpdateModalOpen,
    onOpen: onOpenRequestUpdateModal,
    onClose: onCloseRequestUpdateModal,
  } = useDisclosure();

  const [instance, updateInstance] = usePDF({
    document: <CommoditiesPDF exportPDF={exportPDF} />,
  });

  const exportAsPDF = () => {
    window.open(instance.url || "");
    setSelectedCheckboxItems([]);
  };

  const exportAsCSV = () => {
    const csvHeader = [
      "Name",
      "Location",
      "Retail",
      "Updated",
      "Whole Sale",
      "Updated",
    ];
    const csvRows = exportPDF.map((x: any) => {
      return [
        x.name,
        x?.location?.name || "",
        `${x.retailPrices[0]?.price} / ${x.measurementUnit}`,
        moment(x.retailPrices[0].createdAt).format("MMM. D, Y, h:mm a"),
        `${x.wholeSalePrices[0]?.price} / ${x.measurementUnit}`,
        moment(x.wholeSalePrices[0].createdAt).format("MMM. D, Y, h:mm a"),
      ];
    });

    setExportCSV([csvHeader, ...csvRows]);
    setSelectedCheckboxItems([]);
  };

  useEffect(() => {
    const items = commodities.filter((item: any) =>
      selectedCheckboxItems.includes(item.id)
    );

    setExportPDF(items);
  }, [selectedCheckboxItems]);

  useEffect(() => {
    updateInstance();
  }, [exportPDF, commodities]);

  const categories: any = [];
  if (!isEmpty(PRODUCT_CATEGORIES.map((option: any) => option.options))) {
    PRODUCT_CATEGORIES.forEach((option: any) =>
      option?.options.forEach((category: any) => {
        categories.push(category);
      })
    );
  }

  const requestUpdate = () => {
    return requestPriceUpdate({
      variables: {
        inputs: isEmpty(selectedCheckboxItems)
          ? [{ modelId: selectUpdateItem }]
          : selectedCheckboxItems.map((input: any) => {
              return {
                modelId: input,
              };
            }),
      },
    })
      .then((data: any) => {
        if (data) {
          if (!toast.isActive("active-toast")) {
            toast({
              id: "active-toast",
              title: `Request sent successfully.`,
              status: "success",
              duration: 3000,
              isClosable: true,
              variant: "solid",
            });
          }
          onCloseRequestUpdateModal();
          setCaptchaToken("");
          setSelectedCheckboxItems([]);
          setSelectUpdateItem("");
        }
      })
      .catch((error) => {
        if (!toast.isActive("active-toast")) {
          toast({
            id: "active-toast",
            title: error.message,
            description: "Please, try again.",
            status: "error",
            duration: 2000,
            isClosable: true,
            variant: "solid",
          });
        }
      });
  };
  return (
    <>
      {isLargerThan900 ? (
        <TableContainer my="2">
          {isEmpty(commodities) ? (
            <>
              <Center>
                <Text fontSize="2xl">No commodities found.</Text>
              </Center>
            </>
          ) : (
            <Table variant="simple" size={isLargerThan900 ? "md" : "sm"}>
              <Thead>
                <Tr>
                  <Th>
                    <Checkbox
                      colorScheme="whatsapp"
                      type="checkbox"
                      size="lg"
                      isChecked={!isEmpty(selectedCheckboxItems)}
                      onChange={(e: any) => {
                        if (e.target.checked) {
                          setSelectedCheckboxItems(
                            commodities.map((item: any) => item.id)
                          );
                        } else {
                          setSelectedCheckboxItems([]);
                        }
                      }}
                    />
                  </Th>

                  <Th fontSize="1xl" color="black">
                    {isEmpty(selectedCheckboxItems) ? (
                      "Name"
                    ) : (
                      <Menu size="sm">
                        <MenuButton
                          size="sm"
                          as={Button}
                          rightIcon={<ChevronDownIcon />}
                        >
                          Actions
                        </MenuButton>
                        <MenuList>
                          {isPublic && (
                            <MenuItem onClick={onOpenRequestUpdateModal}>
                              <Icon mr="3" as={MdUpdate} />
                              Request Update
                            </MenuItem>
                          )}
                          <MenuItem onClick={exportAsPDF}>
                            <Icon mr="3" as={FaRegFilePdf} />

                            {!instance.loading ? "Export as PDF" : <Spinner />}
                          </MenuItem>
                          <MenuItem>
                            <Icon mr="3" as={BsFileEarmarkMedical} />

                            <CSVLink
                              filename={moment().format("lll")}
                              data={exportCSV}
                              target="_blank"
                              onClick={exportAsCSV}
                            >
                              Export as CSV
                            </CSVLink>
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  </Th>
                  <Th fontSize="1xl" color="black">
                    Retail
                  </Th>
                  <Th fontSize="1xl" color="black">
                    Whole sale
                  </Th>

                  <Th fontSize="1xl" color="black">
                    Status
                  </Th>
                  <Th fontSize="1xl" color="black">
                    Categories
                  </Th>

                  <Th w="1px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {commodities?.map((item: any) => {
                  const categories_label = categories
                    .filter((category: any) =>
                      item?.categories.includes(category.value)
                    )
                    .map((category: any) => category.label);

                  const requests = get(item, "requests");
                  return (
                    <Tr
                      cursor="pointer"
                      _hover={{
                        bg: "gray.100",
                      }}
                      key={item.id}
                    >
                      <Td>
                        <Checkbox
                          size="lg"
                          colorScheme="whatsapp"
                          isChecked={includes(selectedCheckboxItems, item.id)}
                          onChange={(e: any) => {
                            let updatedSelected = [...selectedCheckboxItems];
                            if (e.target.checked) {
                              updatedSelected.push(item.id);
                            } else {
                              updatedSelected = updatedSelected.filter(
                                (id) => id !== item.id
                              );
                            }
                            setSelectedCheckboxItems(updatedSelected);
                          }}
                        />
                      </Td>
                      <Td color="gray.700" display="flex" alignItems="center">
                        <Image
                          mr="3"
                          boxSize="40px"
                          src={item.imageURL}
                          rounded="sm"
                          fallbackSrc="https://via.placeholder.com/150"
                        />
                        <Box>
                          <Text fontWeight="bold">
                            {capitalize(item?.name) || ""}
                          </Text>
                          {isPublic && (
                            <Text color="gray" fontSize="15px">
                              {truncate(
                                startCase(
                                  item?.user?.establishment || item?.user?.name
                                ),
                                {
                                  length: 40,
                                }
                              )}
                            </Text>
                          )}
                        </Box>
                      </Td>
                      <Td fontWeight="bold" color="gray.700">
                        {PriceView(
                          item?.retailPrices[0],
                          item?.measurementUnit
                        )}
                      </Td>
                      <Td fontWeight="bold" color="gray.700">
                        {PriceView(
                          item?.wholeSalePrices[0],
                          item?.measurementUnit
                        )}
                      </Td>

                      <Td fontWeight="bold" color="gray.700">
                        <Badge
                          colorScheme={
                            item?.status !== "archived" ? "green" : "orange"
                          }
                          ml="2"
                        >
                          {item?.status}
                        </Badge>
                      </Td>

                      <Td fontWeight="bold" color="gray.700">
                        <VStack alignItems="start">
                          {categories_label.map(
                            (label: string, index: number) => (
                              <Badge key={index}>{label}</Badge>
                            )
                          )}
                        </VStack>
                      </Td>

                      <Td w="1px">
                        <Menu size="md">
                          <MenuButton size="md" as={Button}>
                            <BsThreeDotsVertical />
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              icon={<MdDetails />}
                              onClick={() => {
                                if (isPublic) {
                                  navigate("/commodity/public/" + item.id);
                                } else {
                                  navigate("commodity/" + item.id);
                                }
                              }}
                            >
                              Details
                            </MenuItem>
                            {!isPublic && (
                              <MenuItem
                                icon={<MdRequestPage />}
                                onClick={() => {}}
                              >
                                Requests {`(${requests?.length || 0})`}
                              </MenuItem>
                            )}
                            {isPublic ? (
                              <MenuItem
                                onClick={() => {
                                  onOpenRequestUpdateModal();
                                  setSelectedCheckboxItems([]);
                                  setSelectUpdateItem(item.id);
                                }}
                              >
                                <Icon mr="3" as={MdUpdate} />
                                Request Update
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onClick={() => {
                                  onOpenUpdateModal();
                                  setSelectUpdateItem(item.id);
                                }}
                                icon={<EditIcon />}
                              >
                                Update
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      ) : (
        <Box>
          <>
            {!isEmpty(commodities) && (
              <Flex justifyContent="space-between" alignItems="center" p="4">
                <Checkbox
                  colorScheme="whatsapp"
                  type="checkbox"
                  isChecked={!isEmpty(selectedCheckboxItems)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCheckboxItems(
                        commodities.map((item: any) => item.id)
                      );
                    } else {
                      setSelectedCheckboxItems([]);
                    }
                  }}
                  size="lg"
                />

                {!isEmpty(selectedCheckboxItems) && (
                  <Menu size="sm">
                    <MenuButton
                      size="sm"
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                    >
                      Actions
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={onOpenRequestUpdateModal}>
                        <Icon mr="3" as={MdUpdate} />
                        Request Update
                      </MenuItem>
                      <MenuItem onClick={exportAsPDF}>
                        <Icon mr="3" as={FaRegFilePdf} />

                        {!instance.loading ? "Export as PDF" : <Spinner />}
                      </MenuItem>
                      <MenuItem>
                        <Icon mr="3" as={BsFileEarmarkMedical} />

                        <CSVLink
                          filename={moment().format("lll")}
                          data={exportCSV}
                          target="_blank"
                          onClick={exportAsCSV}
                        >
                          Export as CSV
                        </CSVLink>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </Flex>
            )}
          </>
          <Accordion allowToggle>
            {isEmpty(commodities) ? (
              <>
                <Center>
                  <Text fontSize="2xl">No commodities found.</Text>
                </Center>
              </>
            ) : (
              commodities.map((commodity: any) => {
                const categories_label = categories
                  .filter((category: any) =>
                    commodity?.categories.includes(category.value)
                  )
                  .map((category: any) => category.label);

                return (
                  <AccordionItem key={commodity.id} py="2">
                    <AccordionButton _expanded={{ bg: "gray.200" }}>
                      <Checkbox
                        colorScheme="whatsapp"
                        isChecked={includes(
                          selectedCheckboxItems,
                          commodity.id
                        )}
                        onChange={(e: any) => {
                          let updatedSelected = [...selectedCheckboxItems];
                          if (e.target.checked) {
                            updatedSelected.push(commodity.id);
                          } else {
                            updatedSelected = updatedSelected.filter(
                              (id) => id !== commodity.id
                            );
                          }
                          setSelectedCheckboxItems(updatedSelected);
                        }}
                        size="lg"
                        mr="3"
                      />

                      <Flex alignItems="center" justifyContent="start" flex="2">
                        <Image
                          mr="3"
                          boxSize="40px"
                          src={commodity.imageURL}
                          fallbackSrc="https://via.placeholder.com/150"
                          rounded="sm"
                        />

                        <Box textAlign="start">
                          <Text fontWeight="bold" fontSize="14px">
                            {capitalize(commodity?.name) || ""}
                          </Text>
                          {isPublic && (
                            <Text color="gray" fontSize="12px">
                              {truncate(
                                startCase(
                                  commodity?.user?.establishment ||
                                    commodity?.user?.name
                                ),
                                {
                                  length: 17,
                                }
                              )}
                            </Text>
                          )}
                        </Box>
                      </Flex>

                      <Text
                        flex="1"
                        fontWeight="bold"
                        fontSize="sm"
                        textAlign="left"
                        color="gray"
                      >
                        {PriceView(
                          commodity?.retailPrices[0],
                          commodity?.measurementUnit,
                          true
                        )}
                      </Text>
                      <Text
                        flex="1"
                        fontSize="sm"
                        fontWeight="bold"
                        textAlign="left"
                        color="gray"
                      >
                        {PriceView(
                          commodity?.wholeSalePrices[0],
                          commodity?.measurementUnit,
                          true
                        )}
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel pb={4}>
                      <Flex
                        my="3"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text textAlign="left">Retail</Text>
                        <Text fontWeight="bold">
                          {PriceView(
                            commodity?.retailPrices[0],
                            commodity?.measurementUnit
                          )}
                        </Text>
                      </Flex>

                      <Flex
                        my="3"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text textAlign="left">Whole Sale</Text>

                        <Text fontWeight="bold">
                          {PriceView(
                            commodity?.wholeSalePrices[0],
                            commodity?.measurementUnit
                          )}
                        </Text>
                      </Flex>
                      {/* <Flex
                        my="3"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                          <Text textAlign="left">Location</Text>
                          <Text fontWeight="bold">
                            {commodity?.location?.city?.city_name || "-"}
                          </Text>
                        </Flex> */}

                      {isPublic && (
                        <Flex
                          my="3"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Text textAlign="left">Establishment</Text>
                          <Text fontWeight="bold">
                            {startCase(
                              commodity?.user?.establishment ||
                                commodity?.user?.name
                            )}
                          </Text>
                        </Flex>
                      )}

                      <Flex
                        my="3"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text textAlign="left">Status</Text>
                        <Badge
                          colorScheme={
                            commodity?.status !== "archived"
                              ? "green"
                              : "orange"
                          }
                        >
                          {startCase(commodity?.status) || "Active"}
                        </Badge>
                      </Flex>
                      <Flex
                        my="3"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text textAlign="left">Categories</Text>
                        <VStack fontWeight="bold" alignItems="start">
                          {categories_label.map(
                            (label: string, index: number) => (
                              <Badge key={index}>{label}</Badge>
                            )
                          )}
                        </VStack>
                      </Flex>
                      <Stack justifyContent="center">
                        <Button
                          onClick={() => {
                            if (isPublic) {
                              navigate("/commodity/public/" + commodity.id);
                            } else {
                              navigate("commodity/" + commodity.id);
                            }
                          }}
                          colorScheme="orange"
                          size="sm"
                          mr="1"
                          w="full"
                        >
                          Details
                        </Button>

                        {isPublic ? (
                          <Button
                            size="sm"
                            mr="1"
                            w="full"
                            onClick={() => {
                              onOpenRequestUpdateModal();
                              setSelectedCheckboxItems([]);
                              setSelectUpdateItem(commodity.id);
                            }}
                          >
                            Request Update
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              onOpenUpdateModal();
                              setSelectedCheckboxItems([]);
                              setSelectUpdateItem(commodity.id);
                            }}
                            size="sm"
                            ml="1"
                            w="full"
                          >
                            Update
                          </Button>
                        )}
                      </Stack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })
            )}
          </Accordion>
        </Box>
      )}

      {selectUpdateItem && !isPublic && (
        <UpdateCommodity
          id={selectUpdateItem}
          isOpen={isUpdateModalOpen}
          onOpen={onOpenUpdateModal}
          onClose={onCloseUpdateModal}
        />
      )}
      {isPublic && (
        <AlertDialog
          isOpen={isRequestUpdateModalOpen}
          leastDestructiveRef={cancelRef}
          onClose={onCloseRequestUpdateModal}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Request Update
              </AlertDialogHeader>

              <AlertDialogBody>
                <Center>
                  <ReCAPTCHA
                    sitekey="6LeuXoYiAAAAAF3Lc_vrmqkvRxRi3gRvq2lVE3Mw"
                    onChange={(t) => setCaptchaToken(t)}
                  />
                </Center>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onCloseRequestUpdateModal}>
                  Cancel
                </Button>
                <Button
                  colorScheme="green"
                  onClick={requestUpdate}
                  ml={3}
                  isLoading={requestingUpdate}
                  disabled={isEmpty(captchaToken) || requestingUpdate}
                >
                  Request
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </>
  );
};

export const PriceView = (price: any, uom: any, showTime: boolean = true) => {
  return price ? (
    <>
      <Text>
        ₱{price?.price || "-"} / {uom}
      </Text>

      {showTime && (
        <Text fontSize="10px" color="gray">
          {moment(price.createdAt).fromNow()}
        </Text>
      )}
    </>
  ) : (
    "n/a"
  );
};

export default CommoditiesTableContainer;
